import { FormEvent, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Checkbox, ErrorState, Input, MoneyInput, PageHeader, PageLoader, Switch, Textarea, useToast } from '../../../components/ui';
import { useCrumb } from '../../../components/layout/Breadcrumbs/Breadcrumbs';
import { FormSection } from '../../../components/common/Parts/Parts';
import { serverFieldErrors } from '../../../utils/validation';
import { useVendor } from '../hooks/useVendors';
import { EMPTY_VENDOR, fromVendor, toVendorInput, validateVendor, VENDOR_SERVER_FIELDS } from '../schemas/vendorSchema';
import { vendorService } from '../services/vendorService';
import { useForm } from '../../../hooks/useForm';

export default function VendorForm() {
  const { id } = useParams();
  const editing = !!id;
  const navigate = useNavigate();
  const toast = useToast();
  const { form, setForm, set, errors, setErrors } = useForm(EMPTY_VENDOR);
  const [saving, setSaving] = useState(false);
  const { data: vendor, loading, error, reload } = useVendor(id);
  useCrumb(`/vendors/${id}`, vendor?.name);
  useEffect(() => {
    if (vendor) setForm(fromVendor(vendor));
  }, [vendor, setForm]);

  const back = () => navigate(editing ? `/vendors/${id}` : '/vendors');

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const errs = validateVendor(form);
    setErrors(errs);
    if (Object.keys(errs).length) return;

    const body = toVendorInput(form, editing);
    setSaving(true);
    try {
      const saved = id ? await vendorService.update(id, body) : await vendorService.create(body);
      toast.success(editing ? 'Supplier updated' : 'Supplier added');
      navigate(`/vendors/${saved._id}`, { replace: true });
    } catch (err) {
      setErrors(serverFieldErrors(err, VENDOR_SERVER_FIELDS));
      toast.error(err.message);
      setSaving(false);
    }
  };

  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (editing && loading && !vendor) return <PageLoader />;

  const actions = (
    <>
      <Button variant="outline" onClick={back} disabled={saving}>Cancel</Button>
      <Button type="submit" form="vendor-form" leftIcon="check" loading={saving}>{editing ? 'Save changes' : 'Save supplier'}</Button>
    </>
  );

  return (
    <>
      <PageHeader title={editing ? `Edit ${vendor?.name}` : 'Add supplier'} subtitle={editing ? vendor?.vendorCode : 'Shops and dealers you buy material from.'} actions={actions} />
      <form id="vendor-form" onSubmit={submit} noValidate className="space-y-5">
        <FormSection title="Basic details">
          <Input label="Name" required autoFocus value={form.name} onChange={set('name')} error={errors.name} placeholder="Shop or company name" containerClassName="sm:col-span-2" />
          <Input label="Contact person" value={form.contactPerson} onChange={set('contactPerson')} placeholder="Optional" />
          <Input label="Mobile" type="tel" inputMode="numeric" leftIcon="phone" value={form.phone} onChange={set('phone')} error={errors.phone} placeholder="10 digits" />
          <Input label="Email" type="email" value={form.email} onChange={set('email')} error={errors.email} placeholder="Optional" />
          <Input label="GSTIN" value={form.gstin} onChange={(e) => set('gstin')(e.target.value.toUpperCase())} error={errors.gstin} maxLength={15} placeholder="Optional" />
        </FormSection>

        <div className="grid gap-5 xl:grid-cols-2">
          <FormSection title="Address" subtitle="Maharashtra">
            <Textarea label="Shop / street" rows={2} value={form.line} onChange={set('line')} error={errors.line} containerClassName="sm:col-span-2" />
            <Input label="City" value={form.city} onChange={set('city')} error={errors.city} />
            <Input label="Pincode" inputMode="numeric" maxLength={6} value={form.pincode} onChange={set('pincode')} error={errors.pincode} />
          </FormSection>
          <FormSection title="Bank details" subtitle="For paying their bills">
            <Input label="Account name" value={form.accountName} onChange={set('accountName')} />
            <Input label="Bank name" value={form.bankName} onChange={set('bankName')} />
            <Input label="Account number" inputMode="numeric" value={form.accountNo} onChange={set('accountNo')} />
            <Input label="IFSC" value={form.ifsc} onChange={(e) => set('ifsc')(e.target.value.toUpperCase())} maxLength={11} />
            <Input label="UPI ID" value={form.upiId} onChange={set('upiId')} placeholder="name@bank" containerClassName="sm:col-span-2" />
          </FormSection>
        </div>

        <FormSection title="Old balance and notes" subtitle="Only if you owed them, or paid in advance, before this app">
          <MoneyInput label="Amount" value={form.openingBalance} onChange={set('openingBalance')} placeholder="0" hint={form.isAdvance ? 'Money you already paid them in advance' : 'Money you owed them before using this app'} />
          <div className="flex items-start pt-8">
            <Checkbox label="This is an advance" checked={form.isAdvance} onChange={set('isAdvance')} />
          </div>
          <Textarea label="Notes" rows={2} value={form.notes} onChange={set('notes')} maxLength={2000} containerClassName="sm:col-span-2" />
        </FormSection>

        {editing && (
          <Card className="p-5">
            <Switch label="Active supplier" description="Inactive suppliers are hidden when entering purchases" checked={form.isActive} onChange={set('isActive')} />
          </Card>
        )}
        <div className="flex justify-end gap-2">{actions}</div>
      </form>
    </>
  );
}
